import { action, observable, computed } from 'mobx';

import BroadcasterPrivateShowService from 'services/BroadcasterPrivateShowService';
import messagingService from 'services/MessagingService/MessagingService';
import {
  ACCEPT_PRIVATE_SHOW_ERROR,
  BROADCASTER_PRIVATE_SHOW_SETTINGS,
  BROADCASTER_PS_RATINGS,
  PRIVATE_SHOW_USER_NOT_IN_ROOM,
  PROFILE_INFO_RESPONSE,
  SET_ACCEPTED_PENDING_USER_FOR_PRIVATE_SHOW,
  SET_BROADCASTER_PRIVATE_SHOW_INFO,
  SET_IS_PRIVATE_SHOW_REQUESTING_TO_END,
  SET_ORIGINAL_USERNAME_INSIDE_PRIVATE_SHOW,
} from 'services/MessagingService/EventNames';
import ChatStore from 'stores/ChatStore';
import FirebaseStore from 'stores/FirebaseStore';
import UserStore from 'stores/UserStore';
import APIStates from 'enum/APIStates';
import {
  arrayUniqueByKey,
  isStringHTMLTag,
} from 'utils/helpers';


export const C2C_VOLUME_KEY = 'c2cVolume';

const PRIVATE_SHOW_REMOVE_TIMER = 2000;
let initialVolume = 0.5;
const SET_C2C_PLAYER_TIMEOUT = 500;

class BroadcasterPrivateShowStore {
  constructor() {
    this.messagingService = messagingService();
    this.messagingService.on(BROADCASTER_PRIVATE_SHOW_SETTINGS, this.handlePSSettingsResponse);
    this.messagingService.on(PROFILE_INFO_RESPONSE, this.handlePvtRating);
    this.messagingService.on(SET_BROADCASTER_PRIVATE_SHOW_INFO, this.setInsidePrivateShowInfo);
    this.messagingService.on(BROADCASTER_PS_RATINGS, this.setBroadcasterPSRatings);
    this.messagingService.on(SET_ORIGINAL_USERNAME_INSIDE_PRIVATE_SHOW, this.setOriginalUsernameInsidePrivateShow);
    this.messagingService.on(PRIVATE_SHOW_USER_NOT_IN_ROOM, this.handleFailedPS);
    this.messagingService.on(ACCEPT_PRIVATE_SHOW_ERROR, this.handleAcceptPsError);
    this.messagingService.on(SET_IS_PRIVATE_SHOW_REQUESTING_TO_END, this.handlePsRequestingToEnd);
    this.messagingService.on(SET_ACCEPTED_PENDING_USER_FOR_PRIVATE_SHOW, this.setAcceptedPendingUserForPS);

    const storedC2CVolume = localStorage.getItem(C2C_VOLUME_KEY);
    if (storedC2CVolume) {
      this.c2cVolume = parseInt(storedC2CVolume, 10);
      initialVolume = parseInt(storedC2CVolume, 10);
    }
  }

  @observable psPricePerMinute = 6;
  @observable c2cPricePerMinute = 6;
  @observable minCharge = 1;
  @observable allowSpy = false;
  @observable allowCam2Cam = true;
  @observable spyPricePerMinute = 3;
  @observable blocksOfTime = [];
  @observable showTooltip = true;
  @observable privateShowRequests = [];
  @observable ratings = [];
  @observable errors = {};
  @observable insidePrivateShowInfo = {};
  @observable broadcasterPSRatings = [];
  @observable originalUsernameInsidePrivateShow = '';
  @observable extendedBlockOfTimes = {};
  @observable privateShowNotif = {};
  @observable c2cMuted = false;
  @observable c2cVolume = initialVolume;
  @observable privateShowStatus = APIStates.INITIAL;
  @observable privateShowRequestingToEnd = false;
  @observable acceptedPendingUsername = '';

  destructor() {
    this.messagingService.off(BROADCASTER_PRIVATE_SHOW_SETTINGS, this.handlePSSettingsResponse);
    this.messagingService.off(PROFILE_INFO_RESPONSE, this.handlePvtRating);
    this.messagingService.off(SET_BROADCASTER_PRIVATE_SHOW_INFO, this.setInsidePrivateShowInfo);
    this.messagingService.off(BROADCASTER_PS_RATINGS, this.setBroadcasterPSRatings);
    this.messagingService.off(SET_ORIGINAL_USERNAME_INSIDE_PRIVATE_SHOW, this.setOriginalUsernameInsidePrivateShow);
    this.messagingService.off(PRIVATE_SHOW_USER_NOT_IN_ROOM, this.handleFailedPS);
    this.messagingService.off(ACCEPT_PRIVATE_SHOW_ERROR, this.handleAcceptPsError);
    this.messagingService.off(SET_IS_PRIVATE_SHOW_REQUESTING_TO_END, this.handlePsRequestingToEnd);
    this.messagingService.off(SET_ACCEPTED_PENDING_USER_FOR_PRIVATE_SHOW, this.setAcceptedPendingUserForPS);
  }

  /**
   * Method for getting saved(previous) PS settings
   * @param {object} data - data for sending to server
   */

  getBroadcasterPrivateshowSettings = (data) => {
    BroadcasterPrivateShowService.getBroadcasterPrivateshowSettings(data);
  };

  getBroadcasterPrivateshowRatings = (broadcasterUsername, billedUsername) => {
    BroadcasterPrivateShowService.getBroadcasterPrivateshowRatings(broadcasterUsername, billedUsername);
  };

  addBroadcasterPrivateshowRating = (broadcasterUsername, accessHash, billedUsername, rate) => {
    BroadcasterPrivateShowService.addBroadcasterPrivateshowRating(
      broadcasterUsername,
      accessHash,
      billedUsername,
      rate
    );
  };

  updateBroadcasterPrivateshowRating = (broadcasterUsername, accessHash, billedUsername, ratingId, rate) => {
    BroadcasterPrivateShowService.updateBroadcasterPrivateshowRating(
      broadcasterUsername,
      accessHash,
      billedUsername,
      ratingId,
      rate
    );
  };

  @action setAcceptedPendingUserForPS = (username) => {
    this.acceptedPendingUsername = username;
  };

  @action setBroadcasterPSRatings = (data) => {
    this.broadcasterPSRatings = data;
  };

  @action setPsPricePerMinute = (price) => {
    this.psPricePerMinute = price;
  };

  @action setC2cPricePerMinute = (price) => {
    this.c2cPricePerMinute = price;
  };

  @action setMinCharge = (charge) => {
    this.minCharge = charge;
  };

  @action setInsidePrivateShowInfo = (value) => {
    this.insidePrivateShowInfo = { ...this.insidePrivateShowInfo, ...value };
  };

  @action setCam2Cam = (check) => {
    this.allowCam2Cam = check;
  };

  @action setC2CPlayer = (player) => {
    this.c2cPlayer = player;

    try {
      this.c2cMuted = this.c2cPlayer?.muted();
      this.c2cVolume = this.c2cPlayer?.volume();

      this.c2cPlayer?.on('muted', (value) => this.c2cMuted = value);
      this.c2cPlayer?.on('volumeChanged', (value) => this.c2cVolume = value);
    } catch (err) {
      setTimeout(() => this.setC2CPlayer(player), SET_C2C_PLAYER_TIMEOUT);
    }
  };

  @action setC2CMuted = (value) => {
    const prevValue = this.c2cPlayer?.muted();

    if (prevValue !== value) {
      this.c2cPlayer?.muted(value);
    }
  };

  @action toggleC2CMuted = () => {
    this.c2cPlayer?.muted(!this.c2cMuted);
  };

  @action setC2CVolume = (value) => {
    this.c2cPlayer?.volume(value);
    localStorage.setItem(C2C_VOLUME_KEY, value);
  };

  @action setAllowSpy = (check) => {
    this.allowSpy = check;
  };

  @action setSpyPricePerMinute = (price) => {
    this.spyPricePerMinute = price;
  };

  @action setPsTimeBlocksDuration = (duration, index) => {
    this.blocksOfTime[index].duration = parseInt(duration, 10);
    if (parseInt(duration, 10) === 0 || isNaN(duration) || duration === '') {
      this.errors = {
        ...this.errors,
        [index]: {
          price: this.blocksOfTime[index].price,
          c2cPrice: this.blocksOfTime[index].c2cPrice,
          duration: true,
        },
      };
    } else {
      this.errors = {
        ...this.errors,
        [index]: {
          price: this.blocksOfTime[index].price,
          c2cPrice: this.blocksOfTime[index].c2cPrice,
          duration: false,
        },
      };
    }
  };

  @action resetExtendedBlockOfTimes = () => {
    this.extendedBlockOfTimes = {};
  };

  @action setPsTimeBlocksPrice = (price, index, isCam2Cam = false) => {
    if (isCam2Cam) {
      this.blocksOfTime[index].c2cPrice = parseInt(price, 10);
    } else {
      this.blocksOfTime[index].price = parseInt(price, 10);
    }

    if (parseInt(price, 10) === 0 || isNaN(price) || price === '') {
      this.errors = { ...this.errors, [index]: { price: true, duration: this.blocksOfTime[index].duration } };
    } else {
      this.errors = { ...this.errors, [index]: { price: false, duration: this.blocksOfTime[index].duration } };
    }
  };

  @action setPsTimeBlocksDescription = (description, index) => {
    const limit = 80;
    if (!isStringHTMLTag(description)) {
      this.blocksOfTime[index].description = description.slice(0, limit);
    }
  };

  @action setShowTooltip = () => {
    this.showTooltip = false;
  };

  @action updatePSSettings = (data, startPsPayload, broadcasterInSoloPS) => {
    BroadcasterPrivateShowService.updatePSSettings(data, broadcasterInSoloPS);
    if (startPsPayload) {
      const { billedUsername, username, accessHash, cam2cam } = startPsPayload;
      this.privateShowStatus = APIStates.PENDING;
      BroadcasterPrivateShowService.acceptPrivateShow({
        accessHash,
        billedUsername,
        username,
        cam2cam,
      });
    }
  };

  @action handlePSSettingsResponse = (data) => {
    this.allowSpy = data.allowSpy;
    this.allowCam2Cam = data.allowC2C;
    this.psPricePerMinute = data.viewerPPM;
    this.c2cPricePerMinute = data.c2cPPM;
    this.spyPricePerMinute = data.spyPPM;
    this.minCharge = data.payByMinutePSInitialCharge;
    this.blocksOfTime = data.blocksOfTime;
  };

  @action addBlockOfTime = () => {
    this.blocksOfTime.push({ price: 6, duration: 1, c2cPrice: 12, description: '' });
  };

  @action deleteBlockOfTime = (index) => {
    this.blocksOfTime.splice(index, 1);
  };

  @action setIsBlocksActive = (active) => {
    this.isBlocksActive = active;
  };

  @action handlePsRequestingToEnd = (isRequestingToEnd) => {
    this.privateShowRequestingToEnd = isRequestingToEnd;
  };

  @computed get errorBlocksOfTime() {
    const isErrorInInput = Object.values(this.errors).map((error) => {
      if (error.price === true || error.duration === true || isNaN(error.price) || isNaN(error.duration)) {
        return true;
      }

      return false;

    });

    return Object.values(isErrorInInput).includes(true);
  }

  @computed get inPrivateShowButNotSolo() {
    const { newShowsDetails } = FirebaseStore;
    const { user: { username } } = UserStore;

    return newShowsDetails?.ticketHolders[0] !== username?.toLowerCase();
  }

  @computed get inSoloPrivateShow() {
    const { newShowsDetails } = FirebaseStore;
    const { user: { username } } = UserStore;

    return newShowsDetails?.ticketHolders[0] === username?.toLowerCase();
  }

  @computed get privateShowPending() {
    return this.privateShowStatus === APIStates.PENDING;
  }

  /**
   * Method for handling added or removed ps requests
   * @param {string} username
   */
  @action watchPrivateShowRequests = (username) => {
    BroadcasterPrivateShowService.privateShowRequestAdded(username, (snapshot) => {
      const info = snapshot.val();

      const request = {
        username: info.ou,
        balance: info.dt,
        timestamp: info.ts,
        timeout: info.timeout,
        isCam2Cam: Boolean(parseInt(info.c2c, 10)),
        fadeOut: false,
        id: info.id,
      };
      if (info.bt) {
        request.blocksOfTime = info.bt;
        if (info.extend === '1') {
          this.extendedBlockOfTimes = info;
        }
      }
      BroadcasterPrivateShowService.getProfileInfo(info.ou);
      this.privateShowNotif = info;
      this.privateShowRequests = arrayUniqueByKey([...this.privateShowRequests, request], 'username');
    });

    BroadcasterPrivateShowService.privateShowRequestRemoved(username, (snapshot) => {
      const info = snapshot.val();
      const targetRequest = this.privateShowRequests.find((req) => req.id === info.id);
      targetRequest.fadeOut = true;

      setTimeout(() => {
        this.privateShowRequests = this.privateShowRequests.filter((req) => req.id !== info.id);
        this.ratings = this.ratings.filter((rat) => rat.id !== info.id);
      }, PRIVATE_SHOW_REMOVE_TIMER);
    });
  };

  /**
   * Method for setting viewer ratings
   * @param {object} rating contains rating and username
   */
  @action handlePvtRating = (rating) => {
    this.ratings = [...this.ratings, rating];
  };

  /**
   * Method for original username of the viewer participating inside private show
   * @param {string} value
   */
  @action setOriginalUsernameInsidePrivateShow = (value) => {
    this.originalUsernameInsidePrivateShow = value;
    const delay = 500;
    setTimeout(() => this.privateShowStatus = APIStates.LOADED, delay);
  };

  @action resetPrivateShowRequests = () => {
    this.privateShowNotif = {};
    this.privateShowRequests = [];
  };

  @action handleAcceptPsError = () => {
    this.privateShowStatus = APIStates.ERROR;
  };

  /**
   * Checks ps rating
   * @returns {Number}
   */
  getRating = (username) => computed(() => this.ratings.find((rating) => rating.username === username)?.rating).get();

  /**
   * Method for accepting private show request
   */
  @action acceptPrivateShow = (...args) => {
    this.privateShowStatus = APIStates.PENDING;
    BroadcasterPrivateShowService.acceptPrivateShow(...args);
  };

  @action handleFailedPS = (username) => {
    this.privateShowRequests = this.privateShowRequests.filter(
      (req) => req.username?.toLowerCase() !== username?.toLowerCase()
    );
    this.privateShowStatus = APIStates.ERROR;
  };

  /**
   * Method for ending private show
   * @param {string} username
   * @param {string} accessHash
   */
  endPrivateShow = (username, accessHash) => {
    BroadcasterPrivateShowService.endPrivateShow(username, accessHash);
  };

  /**
   * Checks multiple private chat unreads while private show is active
   * @param {string} - targeted username
   * @returns {bool}
   */
  hasUnreadMsgMultiplePrivChats = (targetedUsername) =>
    computed(() => {
      const userNamesList = Object.keys(ChatStore.privateUserChats).filter(
        (key) =>
          key?.toLowerCase() !== UserStore.user.username.toLowerCase() &&
          key?.toLowerCase() !== targetedUsername.toLowerCase()
      );

      return this.checkUnreadMsgs(userNamesList);
    }).get();

  /**
   * Checks for unread messages with user that is participating in the private show
   * @param {string} - targeted username
   * @returns {bool}
   */
  hasUnreadMsgPrivateChat = (targetedUsername) =>
    computed(() => {
      const userNamesList = Object.keys(ChatStore.privateUserChats).filter(
        (key) => key?.toLowerCase() === targetedUsername.toLowerCase()
      );

      return this.checkUnreadMsgs(userNamesList);
    }).get();

  /**
   * Helper function for checking is there unread message
   * @param {array} - usernames list to be checked for unread messages
   * @returns {bool}
   */
  checkUnreadMsgs = (userNamesList) => {
    let hasUnreadMsg = false;
    userNamesList.forEach((key) => {
      const lastMsgNotFromLoggedInUser =
        ChatStore.privateUserChats[key] &&
        ChatStore.privateUserChats[key].messages.length > 0 &&
        ChatStore.privateUserChats[key].messages[ChatStore.privateUserChats[key].messages.length - 1]
          .senderOriginalUsername !== UserStore.user.username;
      if (
        (!ChatStore.activeReadConversations[key] ||
          ChatStore.privateUserChats[key].lastMessageTimestamp >
          ChatStore.activeReadConversations[key].lastReadTimestamp) &&
        lastMsgNotFromLoggedInUser
      ) {
        hasUnreadMsg = true;
      }
    });

    return hasUnreadMsg;
  };
}

export default new BroadcasterPrivateShowStore();
